"use client";
import React, { useMemo, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Progress, Tooltip, Tag } from "antd";
import {
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./DoctorMedicalHistory.css";

import IconArrow2 from "../../../assets/icons/Icon-arrow2.png";
import MedicalHistoryBilanIcon from "../../../assets/icons/svg/medical-history-bilan.svg";
import MedicalHistoryIrmIcon from "../../../assets/icons/svg/medical-history-irm.svg";
import MedicalHistoryEchographyIcon from "../../../assets/icons/svg/medical-history-echography.svg";
import MedicalHistoryRadiographyIcon from "../../../assets/icons/svg/medical-history-radiography.svg";
import MedicalHistoryOrdonnanceIcon from "../../../assets/icons/svg/medical-history-ordonnance.svg";
import MedicalHistoryCompteRenduIcon from "../../../assets/icons/svg/medical-history-compte-rendu.svg";
import MedicalHistoryAutreIcon from "../../../assets/icons/svg/medical-history-autre.svg";
import useExtraction from "../../hooks/useExtraction";

// ========== ICON WRAPPER COMPONENT ==========
const IconWrapper = ({ src, alt = "icon" }) => (
  <img src={src} alt={alt} style={{ width: "24px", height: "24px" }} />
);

const TYPE_ICONS = {
  BILAN_BIOCHIMIQUE: <IconWrapper src={MedicalHistoryBilanIcon} alt="bilan" />,
  IRM: <IconWrapper src={MedicalHistoryIrmIcon} alt="irm" />,
  ECHOGRAPHIE: <IconWrapper src={MedicalHistoryEchographyIcon} alt="echography" />,
  RADIOGRAPHIE: <IconWrapper src={MedicalHistoryRadiographyIcon} alt="radiography" />,
  ORDONNANCE: <IconWrapper src={MedicalHistoryOrdonnanceIcon} alt="ordonnance" />,
  COMPTE_RENDU: <IconWrapper src={MedicalHistoryCompteRenduIcon} alt="compte-rendu" />,
  AUTRE: <IconWrapper src={MedicalHistoryAutreIcon} alt="autre" />,
};

const API_TYPE_ORDER = [
  "BILAN_BIOCHIMIQUE",
  "IRM",
  "ECHOGRAPHIE",
  "RADIOGRAPHIE",
  "ORDONNANCE",
  "COMPTE_RENDU",
  "AUTRE",
];

const buildStatsMap = (statistics) => {
  const byType = statistics?.by_type || {};
  const res = {};
  for (const [typeKey, obj] of Object.entries(byType)) {
    res[typeKey] = Number(obj?.count || 0);
  }
  return res;
};

const Badge = ({ icon }) => (
  <span className="dmh-badge" aria-hidden>
    {icon}
  </span>
);

export default function DoctorMedicalHistory({ onItemClick, userId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  // ✅ patientId depuis props sinon params
  const patientId = String(userId || params.patientId || "").trim();


  // ✅ CORRIGÉ : Passer patientId au hook
  const { statistics, userStorage, storageLoading, getUserStorage } = useExtraction(patientId);

  useEffect(() => {
    if (patientId) {
      getUserStorage();
    }
  }, [getUserStorage, patientId]);

  const statsMap = useMemo(() => buildStatsMap(statistics), [statistics]);

  const documentCount = userStorage?.document_count || 0;
  const maxFiles = userStorage?.max_files || 10;
  const availableFiles = Math.max(
    userStorage?.available_files ?? maxFiles - documentCount,
    0
  );
  const usagePercent = Math.min(
    (documentCount / Math.max(maxFiles, 1)) * 100,
    100
  );

  const getStorageStatus = () => {
    if (usagePercent >= 90) {
      return {
        color: "#ff4d4f",
        status: "error",
        icon: <CloseCircleOutlined />,
        text: t("medicalHistory.almostFull"),
      };
    }
    if (usagePercent >= 60) {
      return {
        color: "#faad14",
        status: "warning",
        icon: <WarningOutlined />,
        text: t("medicalHistory.highUsage"),
      };
    }
    return {
      color: "#52c41a",
      status: "success",
      icon: <CheckCircleOutlined />,
      text: t("medicalHistory.good"),
    };
  };

  const storageStatus = getStorageStatus();

  const items = useMemo(() => {
    return API_TYPE_ORDER.map((typeKey) => {
      const icon = TYPE_ICONS[typeKey] || <IconWrapper src={MedicalHistoryAutreIcon} alt="autre" />;
      const count = statsMap[typeKey] ?? 0;
      return {
        key: typeKey,
        title: t(`medicalHistory.types.${typeKey}.title`),
        desc: t(`medicalHistory.types.${typeKey}.desc`),
        icon,
        count,
      };
    });
  }, [statsMap, t]);

  const goTo = useCallback(
    (typeKey) => {
      if (onItemClick) return onItemClick({ type: typeKey });

      if (!patientId) {
        return;
      }

      // ✅ Navigation vers la route docteur
      navigate(`/doctor/patients/${patientId}/records/${typeKey}`);
    },
    [onItemClick, navigate, patientId]
  );

  return (
    <section className="dmh">
      <header className="dmh-header">
        <h2>{t("medicalHistory.title")}</h2>

        <div className="dmh-storage" aria-label={t("medicalHistory.storageUsed")}>
          <div className="dmh-storage-main">
            <div className="dmh-storage-text">
              <strong style={{ color: storageStatus.color }}>
                {documentCount}
              </strong>
              <span className="dmh-storage-separator">/</span>
              <span className="dmh-storage-quota">
                {maxFiles} {t("medicalHistory.files", { count: maxFiles })}
              </span>
              <Tag
                color={storageStatus.status}
                icon={storageStatus.icon}
                style={{ marginLeft: 8 }}
              >
                {storageStatus.text}
              </Tag>
            </div>

            <Tooltip
              title={t("medicalHistory.storageTooltip", {
                count: documentCount,
                available: availableFiles,
              })}
            >
              <Progress
                percent={usagePercent}
                strokeColor={storageStatus.color}
                showInfo={true}
                format={() => `${documentCount}/${maxFiles}`}
                size="small"
                style={{ width: "250px" }}
              />
            </Tooltip>
          </div>

          {usagePercent >= 60 && (
            <div className="dmh-storage-warning">
              <WarningOutlined />
              <span>
                {usagePercent >= 90
                  ? t("medicalHistory.storageCritical", { available: availableFiles })
                  : t("medicalHistory.storageWarning", { available: availableFiles })}
              </span>
            </div>
          )}
        </div>
      </header>

      <div className="dmh-grid">
        {items.map((item) => (
          <article
            key={item.key}
            className="dmh-card"
            onClick={() => goTo(item.key)}
            tabIndex={0}
            role="button"
            aria-label={t("medicalHistory.openType", { type: item.title })}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                goTo(item.key);
              }
            }}
          >
            <div className="dmh-card-top">
              <Badge icon={item.icon} />
              <h3 className="dmh-title">{item.title}</h3>
            </div>

            <p className="dmh-desc">{item.desc}</p>

            <div className="dmh-card-bottom">
              <span className="dmh-count">
                {String(item.count).padStart(2, "0")}{" "}
                {t("medicalHistory.test", { count: item.count })}
                {item.count === 0 && (
                  <span className="dmh-empty"> ({t("medicalHistory.empty")})</span>
                )}
              </span>

              <button
                className="dmh-arrow"
                aria-label={t("medicalHistory.openType", { type: item.title })}
                onClick={(e) => {
                  e.stopPropagation();
                  goTo(item.key);
                }}
              >
                <img src={IconArrow2} alt="" className="dmh-arrow-icon" />
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
